// components/AudioList.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./App.css"; // Reuse existing CSS
import { ServerUrl } from "../config";
import { decodedToken } from "./utils";

function AudioList() {
  const navigate = useNavigate();
  const [audioDetails, setAudioDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audioBuffers, setAudioBuffers] = useState({});
  const [activeAudio, setActiveAudio] = useState(null);
  const [playing, setPlaying] = useState({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const emailUser = localStorage.getItem("email");
  const decoded = decodedToken();
  const userRole = decoded?.role || "user";

  const fetchAudioDetails = async (pageNum, pageLimit) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${ServerUrl}api/audios?page=${pageNum}&limit=${pageLimit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        const { audios, totalPages: total } = response.data;
        setAudioDetails(audios);
        setTotalPages(total);
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to fetch audio details", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleAudio = async (fileName, audioId) => {
    if (activeAudio && activeAudio.id !== audioId && activeAudio.instance) {
      activeAudio.instance.pause();
      activeAudio.instance.currentTime = 0;
      setPlaying((prev) => ({ ...prev, [activeAudio.id]: false }));
    }

    if (!audioBuffers[audioId]) {
      setLoading((prev) => ({ ...prev, [audioId]: true }));
      try {
        const response = await axios.post(
          `${ServerUrl}stream_audio`,
          { file: fileName },
          { responseType: "blob" }
        );

        const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        setAudioBuffers((prev) => ({ ...prev, [audioId]: audioUrl }));
        setActiveAudio({ id: audioId, instance: audio });
        audio.play();
        setPlaying((prev) => ({ ...prev, [audioId]: true }));
      } catch (error) {
        Swal.fire("Error", "Failed to load audio: " + error.message, "error");
      } finally {
        setLoading((prev) => ({ ...prev, [audioId]: false }));
      }
    } else {
      const audio =
        activeAudio?.id === audioId
          ? activeAudio.instance
          : new Audio(audioBuffers[audioId]);
      const isPlaying = playing[audioId];

      if (isPlaying) {
        audio.pause();
        audio.currentTime = 0;
        setPlaying((prev) => ({ ...prev, [audioId]: false }));
        setActiveAudio(null);
      } else {
        setActiveAudio({ id: audioId, instance: audio });
        audio.play().catch((err) => console.error("Play error:", err));
        setPlaying((prev) => ({ ...prev, [audioId]: true }));
      }
    }
  };

  const handleBack = () => {
    navigate("/audio");
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setLimit(newLimit);
    setPage(1);
    fetchAudioDetails(1, newLimit);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      fetchAudioDetails(newPage, limit);
    }
  };

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const pages = [];
    let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  useEffect(() => {
    fetchAudioDetails(page, limit);
  }, []); // Initial fetch

  return (
    <div className="w-screen min-h-screen bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 flex justify-center items-start p-4">
      <div className="bg-white p-6 sm:p-8 md:p-12 rounded-xl shadow-xl space-y-6 w-full max-w-5xl">
        <h1 className="text-2xl sm:text-3xl font-semibold text-center mb-6 text-gray-800">
          Uploaded Audio List
        </h1>
        <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 items-center justify-between">
          <button
            onClick={handleBack}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 w-full sm:w-auto"
          >
            Back to Audio Upload
          </button>
          <select
            value={limit}
            onChange={handleLimitChange}
            className="w-full sm:w-32 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>

        <div className="mt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Uploaded Audios
          </h2>
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="loader"></div>
            </div>
          ) : audioDetails.length === 0 ? (
            <p className="text-center text-gray-600">No audios found.</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {audioDetails.map((audio) => (
                <div
                  key={audio._id}
                  className="bg-gray-50 p-4 rounded-lg shadow-md flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
                >
                  <div className="flex-1">
                    <p>
                      <strong>Language:</strong> {audio.audioLanguage}
                    </p>
                    <p>
                      <strong>Gender & Age:</strong> {audio.audioLabel}
                    </p>
                    <p>
                      <strong>Audio Text:</strong> {audio.audiotext}
                    </p>
                    <p>
                      <strong>File Name:</strong> {audio.fileName}
                    </p>
                    {userRole === 'admin' && (
                      <p>
                        <strong>Uploaded By:</strong> {audio.uploaderEmail || audio.email}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center">
                    {loading[audio._id] ? (
                      <div className="loader"></div>
                    ) : (
                      <button
                        onClick={() => toggleAudio(audio.fileName, audio._id)}
                        className={`px-4 py-2 rounded text-white ${
                          playing[audio._id]
                            ? "bg-red-500 hover:bg-red-600"
                            : "bg-green-500 hover:bg-green-600"
                        }`}
                      >
                        {playing[audio._id] ? "Stop" : "Play"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Enhanced Pagination UI */}
        <div className="flex flex-col items-center mt-6 space-y-4">
          <div className="flex items-center justify-between w-full sm:w-auto gap-4">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className={`flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors duration-200 ${
                page === 1
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages || totalPages === 0}
              className={`flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors duration-200 ${
                page === totalPages || totalPages === 0
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              Next
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
          {totalPages > 0 && (
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {getPageNumbers().map((pageNum, index) => (
                <button
                  key={index}
                  onClick={() =>
                    typeof pageNum === "number" && handlePageChange(pageNum)
                  }
                  className={`px-3 py-1 rounded-lg font-medium transition-colors duration-200 ${
                    pageNum === page
                      ? "bg-blue-600 text-white"
                      : pageNum === "..."
                      ? "bg-gray-200 text-gray-700 cursor-default"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  disabled={pageNum === "..."}
                >
                  {pageNum}
                </button>
              ))}
            </div>
          )}
          <p className="text-sm text-gray-600">
            Page {page} of {totalPages || 1}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AudioList;